import React, { useRef, useState } from 'react';
import { StyleProp, TextStyle, View, ViewStyle } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from './MyAppText';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { translate } from '../services/translate';
import { platformSettingsService } from '../services/central-api/platform-settings';
import inputStylesheet from '../styles/inputStyles';

interface LocationDropdownProps {
    label?: string;
    labelFontSize?: number;
    labelStyle?: StyleProp<TextStyle>;
    address: string;
    onAddressChange: React.Dispatch<React.SetStateAction<string>>;
    onInputChange?: React.Dispatch<React.SetStateAction<string>>;
    onPinChange?: (pos: LatLng | undefined) => void;
    viewStyle?: StyleProp<ViewStyle>;
    disabled?: boolean;
    required?: boolean;
}

export default function LocationSelector({ label, address, onAddressChange, onInputChange, onPinChange, viewStyle, disabled = false, required }: LocationDropdownProps) {
    const { theme } = useStyles(stylesheet);
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { data } = platformSettingsService.useMapCentralPoint();

    const geocoderRef = useRef<google.maps.Geocoder>();
    function getGeocoder() {
        if (!geocoderRef.current) {
            geocoderRef.current = new google.maps.Geocoder();
        }
        return geocoderRef.current;
    }

    async function resolvePin(request: { placeId: string; } | { address: string; }) {
        if (!onPinChange) {
            return;
        }

        try {
            const result = await getGeocoder().geocode(request);
            const place = result.results[0];
            onPinChange(place ? { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() } : undefined);
        } catch (err) {
            console.error(err);
            onPinChange(undefined);
        }
    }

    const inputStyle = defaultInputStyle.input({
        disabled: disabled,
        placeholder: !address,

    });

    return (
        <View style={[defaultInputStyle.container, viewStyle]}>
            {label &&
                <View style={defaultInputStyle.labelContainer}>
                    {label && <MyAppText style={defaultInputStyle.label}>{label}</MyAppText>}
                    {required && label ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                </View>
            }
            <GooglePlacesAutocomplete
                apiOptions={{
                    language: 'pt-BR',
                    region: 'BR',
                }}
                autocompletionRequest={{
                    // default value refers to the city of sao paulo
                    location: {
                        lat: data?.latitude ?? -23.533773,
                        lng: data?.longitude ?? -46.62529,
                    },
                    radius: data?.radius ?? 35,
                    componentRestrictions: { country: 'br' },
                }}
                selectProps={{
                    onChange: (value) => {
                        setIsMenuOpen(false);

                        if (value) {
                            onAddressChange(value.label);
                            resolvePin({ placeId: value.value.place_id });
                        } else {
                            onAddressChange('');
                            onPinChange?.(undefined);
                        }
                    },
                    onInputChange: (value, actionMeta) => {
                        if (actionMeta.action === 'input-change') {
                            if (onInputChange) {
                                onInputChange(value);
                            }
                            onAddressChange(value);
                        }
                    },
                    onMenuOpen: () => setIsMenuOpen(true),
                    onMenuClose: () => setIsMenuOpen(false),
                    value: (!isMenuOpen && address) ? { label: address, value: {} } : null,
                    inputValue: isMenuOpen ? address : undefined,
                    menuIsOpen: isMenuOpen,
                    placeholder: translate('filter'),
                    loadingMessage: () => translate('loading'),
                    noOptionsMessage: () => translate('nothingToShow'),
                    isDisabled: disabled,
                    styles: {
                        option: (base, state) => ({
                            ...base,
                            color: state.isFocused
                                ? theme.colors.hoverText
                                : inputStyle.color,
                            backgroundColor: state.isFocused
                                ? theme.colors.primary['200']
                                : inputStyle.backgroundColor,
                            cursor: 'pointer',
                            fontFamily: inputStyle.fontFamily,
                            transition: 'background-color 0.2s ease, color 0.2s ease',
                            fontWeight: inputStyle.fontWeight,
                            fontSize: inputStyle.fontSize,
                        }),
                        singleValue(base) {
                            base.color = inputStyle.color;
                            base.fontFamily = inputStyle.fontFamily;
                            base.fontWeight = inputStyle.fontWeight;
                            base.fontSize = inputStyle.fontSize;
                            base.whiteSpace = 'break-spaces';
                            return base;
                        },
                        placeholder(base) {
                            base.color = inputStyle.color;
                            base.fontFamily = inputStyle.fontFamily;
                            base.fontWeight = inputStyle.fontWeight;
                            base.fontSize = inputStyle.fontSize;
                            return base;
                        },
                        input(base) {
                            base.cursor = disabled ? 'not-allowed' : 'text';
                            base.color = inputStyle.color;
                            base.fontFamily = inputStyle.fontFamily;
                            base.fontWeight = inputStyle.fontWeight;
                            base.fontSize = inputStyle.fontSize;
                            return base;
                        },
                        control(base) {
                            base.backgroundColor = inputStyle.backgroundColor;
                            base.borderColor = inputStyle.borderColor;
                            base.minHeight = inputStyle.height;
                            base.color = inputStyle.color;
                            base.fontFamily = inputStyle.fontFamily;
                            base.fontWeight = inputStyle.fontWeight;
                            base.fontSize = inputStyle.fontSize;
                            return base;
                        },
                        menuList(base) {
                            base.color = inputStyle.color;
                            base.fontFamily = inputStyle.fontFamily;
                            base.fontWeight = inputStyle.fontWeight;
                            base.fontSize = inputStyle.fontSize;
                            base.backgroundColor = inputStyle.backgroundColor;
                            return base;
                        }
                    }
                }}
            />
        </View>
    );
}

const stylesheet = createStyleSheet(theme => ({
    label: (fontSize: number) => ({
        color: theme.colors.labelColor,
        fontSize,
        height: 25,
    }),
}));
